// src/utils/sticker/generate-sticker.ts
// On-device background removal (web @imgly WASM / iOS Vision). No data leaves the device.
//
// The sticker we store is the plain cut-out subject (transparent background, NO
// baked border), and it is rendered borderless everywhere (see StickerImage).
import { removeBackground } from './remove-background';
import { assessStickerable } from './stickerable';

export type StickerResult = { stickerable: boolean; sticker: string | null };

const MAX_DIM = 800;       // cap output size (matches image-compression default)
const WEBP_QUALITY = 0.85;

async function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Cut out the subject. The returned data URL is the transparent cut-out; the
 * sticker border is applied at render time (theme-aware), not baked here.
 *
 * Failure semantics matter for the caller's retry logic:
 * - **Infra failures PROPAGATE (throw):** model fetch blocked/offline, image
 *   decode error, missing canvas. These are transient and retryable, so we let
 *   them reject rather than masking them as a verdict.
 * - **`{ stickerable: false }` is a VERDICT:** the model ran successfully and the
 *   heuristic judged the image not a distinct object. This is legitimately
 *   permanent and the caller persists it (no retry).
 */
export async function generateSticker(base64Image: string): Promise<StickerResult> {
  // Cut out the subject (web @imgly / iOS Vision). Throws on infra failure —
  // intentionally not caught here. null = no foreground subject (not stickerable).
  const cutoutBlob = await removeBackground(base64Image);
  if (!cutoutBlob) {
    return { stickerable: false, sticker: null };
  }
  const cutoutUrl = URL.createObjectURL(cutoutBlob);
  try {
    const cutout = await loadImage(cutoutUrl);

    // Downscale to MAX_DIM for cheap pixel work.
    let w = cutout.naturalWidth;
    let h = cutout.naturalHeight;
    if (Math.max(w, h) > MAX_DIM) {
      const s = MAX_DIM / Math.max(w, h);
      w = Math.round(w * s);
      h = Math.round(h * s);
    }

    const work = document.createElement('canvas');
    work.width = w;
    work.height = h;
    const wctx = work.getContext('2d')!;
    wctx.drawImage(cutout, 0, 0, w, h);
    const cutoutData = wctx.getImageData(0, 0, w, h);

    // Extract alpha channel for the stickerable heuristic.
    const alpha = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) alpha[i] = cutoutData.data[i * 4 + 3];

    // Verdict: model ran, but this isn't a distinct object. Permanent (no retry).
    if (!assessStickerable(alpha, w, h)) {
      return { stickerable: false, sticker: null };
    }

    // Store the plain cut-out. WebP is preferred for smaller file size; on
    // platforms without canvas WebP support (notably the iOS WebView / Safari)
    // the browser transparently returns PNG instead — fine, the data URL is
    // self-describing and alpha is preserved either way.
    const sticker = work.toDataURL('image/webp', WEBP_QUALITY);
    return { stickerable: true, sticker };
  } finally {
    URL.revokeObjectURL(cutoutUrl);
  }
}
